import { useState } from 'react';
import { describeError } from '../lib/api';
import { useRequestSignInLink } from '../lib/queries';

const FIELD =
  'text-body rounded-button mt-2 min-h-touch w-full border bg-surface px-4 text-ink placeholder:text-ink-4';
const BUTTON =
  'text-button rounded-button mt-3 flex min-h-touch w-full items-center justify-center bg-ink px-4 font-semibold text-white disabled:opacity-60';

/**
 * The gate: while there is no session this is the only screen the app renders, whatever the URL
 * says, because the client's decision is that nothing is readable before signing in.
 *
 * ACC-01: an emailed link and no password. The screen deliberately says the same thing whatever
 * address was typed — whether somebody is registered is not information this form gives away
 * (ACC-05) — so the helpful part is telling them what to do when no mail arrives.
 */
export function SignInPage() {
  const [email, setEmail] = useState('');
  const [invalid, setInvalid] = useState(false);
  const request = useRequestSignInLink();
  const sent = request.data;

  const submit = (form: React.FormEvent) => {
    form.preventDefault();
    const address = email.trim();

    // Checked here as well as by the API so a typo does not cost a round trip on venue wifi.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(address)) {
      setInvalid(true);
      return;
    }

    setInvalid(false);
    request.mutate(address);
  };

  return (
    <main className="px-gutter flex min-h-dvh flex-col pt-8 pb-6">
      <img
        src="/logo-lockup.png"
        alt="Go For The Greens"
        className="h-auto w-[196px] max-w-full"
        width={400}
        height={275}
      />

      {sent ? (
        <>
          <h1 className="font-display text-session-title mt-6 font-semibold text-ink">
            Check your email
          </h1>
          <p className="text-body mt-2 text-ink-3">
            If <span className="font-semibold text-ink">{email.trim()}</span> is the address you
            registered with, a sign-in link is on its way. It works once and expires in 30 minutes.
          </p>

          {/* ACC-05, the useful half: they cannot be told whether the address is registered, so
              they are told what to do about it either way. */}
          <p className="text-subtitle rounded-card mt-4 border border-border bg-sand p-4 text-ink-3">
            Nothing arriving? Check the junk folder, then try the address on your Eventbrite
            confirmation — many attendees register with a different one. If it still does not
            arrive, the organizers can look you up.
          </p>

          {sent.devLink ? (
            <div className="rounded-card mt-4 border border-warn-border bg-warn-bg p-4">
              <p className="text-subtitle font-semibold text-warn-text">
                No mail is being sent on this build
              </p>
              <p className="text-subtitle mt-1 text-warn-text">
                The mail transport is the local one, so the link is shown here instead. A deployment
                never reaches this state.
              </p>
              <a
                href={sent.devLink}
                className="text-body mt-2 block break-all font-semibold text-ink underline"
              >
                Open the sign-in link
              </a>
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => {
              request.reset();
              setEmail('');
            }}
            className="text-button rounded-button mt-4 flex min-h-touch w-full items-center justify-center border border-border-input bg-surface px-4 font-semibold text-ink"
          >
            Use a different address
          </button>
        </>
      ) : (
        <>
          <h1 className="font-display text-session-title mt-6 font-semibold text-ink">
            Welcome back
          </h1>
          <p className="text-body mt-2 text-ink-3">
            No password to remember. We send a link that keeps you signed in for 90 days.
          </p>

          <form onSubmit={submit} className="mt-6" noValidate>
            <label
              htmlFor="email"
              className="text-eyebrow tracking-eyebrow block font-bold text-ink-3 uppercase"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              value={email}
              onChange={(item) => setEmail(item.target.value)}
              placeholder="you@company.com"
              aria-invalid={invalid}
              aria-describedby={invalid ? 'email-error' : undefined}
              className={`${FIELD} ${invalid ? 'border-alert-border' : 'border-border-input'}`}
            />

            {invalid && (
              <p
                id="email-error"
                role="alert"
                className="text-subtitle rounded-button mt-2 border border-alert-border bg-alert-bg px-3 py-2 text-alert-text"
              >
                That does not look like an email address.
              </p>
            )}

            {request.error && (
              <p
                role="alert"
                className="text-subtitle rounded-button mt-2 border border-alert-border bg-alert-bg px-3 py-2 text-alert-text"
              >
                {describeError(request.error, 'The link could not be sent. Try again.')}
              </p>
            )}

            <button type="submit" disabled={request.isPending} className={BUTTON}>
              {request.isPending ? 'Sending…' : 'Send my link'}
            </button>
          </form>

          <p className="text-subtitle mt-3 text-ink-4">
            The program, the presenters and the venue are open to everyone — signing in is what adds
            your schedule, your profile and who else is coming.
          </p>
        </>
      )}

      <p className="text-eyebrow mt-auto flex items-center gap-2 pt-8 text-ink-4">
        <span className="size-2 shrink-0 rounded-full bg-brand" aria-hidden="true" />
        Powered by ApolloRise
      </p>
    </main>
  );
}
